// @flow

import * as React from 'react';
import Relay from 'react-relay/classic';
import useRelay from 'react-router-relay';
import {
  Router,
  Route, Redirect, IndexRoute, IndexRedirect,
  browserHistory, applyRouterMiddleware
} from 'react-router';

// The components used in the router
import Main, { DefaultDocumentTitle } from './components/Main';
import SectionLoader from './components/shared/SectionLoader';
import APIAccessTokenCodeAuthorize from './components/api_access_token_code/APIAccessTokenCodeAuthorize';
import BuildCommentsList from './components/build/CommentsList';
import OrganizationShow from './components/OrganizationShow';
import AgentIndex from './components/agent/Index';
import AgentShow from './components/agent/Show';
import AgentJobs from './components/agent/Jobs';
import TeamIndex from './components/team/Index';
import TeamShow from './components/team/Show';
import TeamMembers from './components/team/Members';
import TeamPipelines from './components/team/Pipelines';

import MemberIndex from './components/member/Index';
import MemberShow from './components/member/Show';
import MemberEdit from './components/member/Edit';
import MemberTeams from './components/member/Teams';

import PipelineScheduleIndex from './components/pipeline/schedules/Index';
import PipelineScheduleNew from './components/pipeline/schedules/New';
import PipelineScheduleShow from './components/pipeline/schedules/Show';
import PipelineScheduleEdit from './components/pipeline/schedules/Edit';
import PipelineTeamIndex from './components/pipeline/teams/Index';
import PipelineNewTeams from './components/pipeline/New/PipelineNewTeams';
import AuditLogSection from './components/audit_log/Section';
import AuditLogIndex from './components/audit_log/Index';
import AuditLogExport from './components/audit_log/Export';
import JobIndex from './components/job/Index';

import TwoFactorIndex from './components/user/TwoFactor';

import GraphQLExplorer from './components/user/graphql/GraphQLExplorer';
import GraphQLExplorerConsole from './components/user/graphql/GraphQLExplorerConsole';
import GraphQLExplorerDocumentation from './components/user/graphql/GraphQLExplorerDocumentation';
import GraphQLExplorerDocumentationField from './components/user/graphql/GraphQLExplorerDocumentationField';
import GraphQLExplorerDocumentationType from './components/user/graphql/GraphQLExplorerDocumentationType';
import GraphQLExplorerExamples from './components/user/graphql/GraphQLExplorerExamples';

import * as AgentQuery from './queries/Agent';
import * as BuildQuery from './queries/Build';
import * as OrganizationQuery from './queries/Organization';
import * as OrganizationMemberQuery from './queries/OrganizationMember';
import * as PipelineQuery from './queries/Pipeline';
import * as PipelineScheduleQuery from './queries/PipelineSchedule';
import * as TeamQuery from './queries/Team';
import * as ViewerQuery from './queries/Viewer';
import * as APIAccessTokenCodeQuery from './queries/APIAccessTokenCode';

import FlashesStore from './stores/FlashesStore';

import { trackPageView, setCustomUrl, setReferrerUrl } from './lib/matomo';

const renderSectionLoading = (route) => {
  if (!route.props) {
    return (
      <SectionLoader />
    );
  }

  return React.cloneElement(route.element, route.props);
};

// Only require the `organization` fragment if there's an ":organization"
// param in the URL
const getMainQueries = ({ params }) => {
  if (params.organization) {
    return {
      viewer: ViewerQuery.query,
      organization: OrganizationQuery.query
    };
  }

  return {
    viewer: ViewerQuery.query
  };
};

// Since you can't pass `undefined` as a property to a Relay.Container, and
// not all pages have the `organization` available, we need to change it to
// `null` if it's not available.
const renderMain = (route) => {
  if (route.props) {
    route.props.organization = route.props.organization || null;

    return React.cloneElement(route.element, route.props);
  }
};

/// Adds a MutationObserver to the `<title>` element, and waits for its contents to change,
/// or for an optionally-specified timeout.
///
/// Note for future players; the default `timeout` is long because it takes Firefox a while to
/// warm up from a cold boot, for whatever reason, and I think we prefer correct titles to
/// high-precision time-of-view information.
const titleChangeOrTimeout = (timeout: number = 4000) => new Promise((resolve, reject) => {
  const titleElement = document.querySelector('title');

  if (!titleElement) {
    return reject(new TypeError('Document title element is missing'));
  }

  // A callback to abort the listener once it's done
  const cleanup = () => {
    titleObserver.disconnect();
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
    }
  };

  // A callback to complete the
  const complete = (title: string = document.title) => {
    cleanup();
    resolve(title);
  };

  const titleObserver = new MutationObserver((mutations) => {
    // Find the first mutation which is the title element, and which isn't the default title.
    // If the default title is _really_ the document title, then we catch it in a timeout anyway.
    const mutation = mutations.find((mutation) => mutation.target === titleElement && mutation.target.innerText !== DefaultDocumentTitle);

    if (!mutation) {
      return;
    }

    complete(mutation.target.innerText);
  });

  titleObserver.observe(
    titleElement,
    {
      characterData: true,
      childList: true,
      subtree: true
    }
  );

  const timeoutHandle = setTimeout(complete, timeout);
});

type RouterState = {
  location: {
    pathname: string,
    search: string,
    hash: string
  },
  routes: Array<{
    to?: string,
    from?: string
  }>
};

const getURLFromRouterState = (state: RouterState) => window.location.origin + state.location.pathname + state.location.search + state.location.hash;

let prevStateBeforeRedirects: ?RouterState = undefined;

/// This handler is called by the `onChange` handler,
/// and is called for all but the initial route
const routeChangeHandler = (prevState: RouterState, nextState: RouterState) => {
  const nextStateDeepestRoute = nextState.routes[nextState.routes.length - 1];

  // When we hit a redirect route, store the `prevState` from before we hit it.
  if (nextStateDeepestRoute.from || nextStateDeepestRoute.to) {
    // Keep the initial redirect if we hit more than one.
    if (!prevStateBeforeRedirects) {
      prevStateBeforeRedirects = prevState;
    }

    // And abort reporting it.
    return;
  }

  let prevStateMutable: RouterState = prevState;

  const prevStateDeepestRoute = prevState.routes[prevState.routes.length - 1];

  // Check if the previous state was a redirect, and thus if we should consider that the referrer
  if ((prevStateDeepestRoute.from || prevStateDeepestRoute.to) && prevStateBeforeRedirects) {
    prevStateMutable = prevStateBeforeRedirects;
    prevStateBeforeRedirects = undefined;
  }

  // Reset flashes on route changes
  FlashesStore.reset();

  // Calculate fully-qualified route URLs from the passed states
  const referringRoute = getURLFromRouterState(prevStateMutable);
  const newRoute = getURLFromRouterState(nextState);

  // Because the route changes before everything is rendered,
  // we can't submit our analytics events yet. Wait for the
  // title to change, or a timeout to be hit.
  titleChangeOrTimeout().then(() => {
    // Fire off a pageView event on history changes.
    setReferrerUrl(referringRoute);
    setCustomUrl(newRoute);
    trackPageView(document.title);
  });
};

/// This handler is called by the `onEnter`` handler,
/// and is called for the initial state only
const routeEnterHandler = (nextState: RouterState) => {
  // Calculate fully-qualified route URL from the passed state
  const newRoute = getURLFromRouterState(nextState);

  // Because the route changes before everything is rendered,
  // we can't submit our analytics events yet. Wait for the
  // title to change, or a timeout to be hit.
  titleChangeOrTimeout().then(() => {
    // Fire off a pageView event on history changes.
    setCustomUrl(newRoute);
    trackPageView(document.title);
  });
};

export default (
  <Router history={browserHistory} render={applyRouterMiddleware(useRelay)} environment={Relay.Store}>
    <Route path="/:organization/:pipeline/builds/:number" component={BuildCommentsList} queries={{ viewer: ViewerQuery.query, build: BuildQuery.query }} prepareParams={BuildQuery.prepareParams} />
    <Route path="/organizations/:organization/pipelines/new" component={PipelineNewTeams} queries={{ organization: OrganizationQuery.query }} render={renderSectionLoading} />
    <Route path="/organizations/:organization/pipelines" component={PipelineNewTeams} queries={{ organization: OrganizationQuery.query }} render={renderSectionLoading} />
    <Route path="/user/two-factor" component={TwoFactorIndex} />

    <Route path="organizations/:organization/audit-log">
      <Route component={AuditLogSection} queries={{ organization: OrganizationQuery.query }}>
        <IndexRoute component={AuditLogIndex} render={renderSectionLoading} queries={{ organization: OrganizationQuery.query }} />
        <Route path="export" component={AuditLogExport} render={renderSectionLoading} queries={{ organization: OrganizationQuery.query }} />
      </Route>
    </Route>

    <Route path="organizations/:organization/users">
      <IndexRoute component={MemberIndex} queries={{ organization: OrganizationQuery.query }} render={renderSectionLoading} />
      <Route path=":organizationMember" component={MemberShow} queries={{ organizationMember: OrganizationMemberQuery.query }} prepareParams={OrganizationMemberQuery.prepareParams} render={renderSectionLoading}>
        <IndexRedirect to="settings" />

        <Route path="settings" component={MemberEdit} queries={{ viewer: ViewerQuery.query, organizationMember: OrganizationMemberQuery.query }} prepareParams={OrganizationMemberQuery.prepareParams} render={renderSectionLoading} />
        <Route path="teams" component={MemberTeams} queries={{ organizationMember: OrganizationMemberQuery.query }} prepareParams={OrganizationMemberQuery.prepareParams} render={renderSectionLoading} />
      </Route>
    </Route>

    <Route path="organizations/:organization/teams">
      <IndexRoute component={TeamIndex} queries={{ organization: OrganizationQuery.query }} render={renderSectionLoading} />
      <Route path=":team" component={TeamShow} queries={{ team: TeamQuery.query }} prepareParams={TeamQuery.prepareParams} render={renderSectionLoading}>
        <IndexRedirect to="members" />
        <Redirect from="edit" to="settings" />
        <Route path="members" component={TeamMembers} queries={{ team: TeamQuery.query }} prepareParams={TeamQuery.prepareParams} render={renderSectionLoading} />
        <Route path="pipelines" component={TeamPipelines} queries={{ team: TeamQuery.query }} prepareParams={TeamQuery.prepareParams} render={renderSectionLoading} />
      </Route>
    </Route>

    <Route path=":organization/:pipeline/settings/schedules">
      <IndexRoute component={PipelineScheduleIndex} queries={{ pipeline: PipelineQuery.query }} prepareParams={PipelineQuery.prepareParams} render={renderSectionLoading} />
      <Route path="new" component={PipelineScheduleNew} queries={{ pipeline: PipelineQuery.query }} prepareParams={PipelineQuery.prepareParams} render={renderSectionLoading} />
      <Route path=":schedule" component={PipelineScheduleShow} queries={{ viewer: ViewerQuery.query, pipelineSchedule: PipelineScheduleQuery.query }} prepareParams={PipelineScheduleQuery.prepareParams} render={renderSectionLoading} />
      <Route path=":schedule/edit" component={PipelineScheduleEdit} queries={{ pipelineSchedule: PipelineScheduleQuery.query }} prepareParams={PipelineScheduleQuery.prepareParams} render={renderSectionLoading} />
    </Route>

    <Route path=":organization/:pipeline/settings/teams">
      <IndexRoute component={PipelineTeamIndex} queries={{ pipeline: PipelineQuery.query }} prepareParams={PipelineQuery.prepareParams} render={renderSectionLoading} />
    </Route>

    <Route path="/user/graphql" component={GraphQLExplorer}>
      <IndexRedirect to="console" />
      <Route path="console">
        <IndexRoute component={GraphQLExplorerConsole} />
        <Route path=":snippet" component={GraphQLExplorerConsole} />
      </Route>
      <Route path="documentation">
        <IndexRoute component={GraphQLExplorerDocumentation} />
        <Route path="query/:field" component={GraphQLExplorerDocumentationField} />
        <Route path="mutation/:field" component={GraphQLExplorerDocumentationField} />
        <Route path="type/:name" component={GraphQLExplorerDocumentationType} />
      </Route>
      <Route path="examples" component={GraphQLExplorerExamples} />
    </Route>

    <Route path="/:organization" component={OrganizationShow} />

    <Route path="/organizations/:organization/agents">
      <IndexRoute component={AgentIndex} queries={{ organization: OrganizationQuery.query }} render={renderSectionLoading} />
      <Route path=":agent" component={AgentShow} queries={{ agent: AgentQuery.query }} prepareParams={AgentQuery.prepareParams} render={renderSectionLoading} />
      <Route path=":agent/jobs" component={AgentJobs} queries={{ agent: AgentQuery.query }} prepareParams={AgentQuery.prepareParams} render={renderSectionLoading} />
    </Route>

    <Route path="/organizations/:organization/jobs">
      <IndexRoute component={JobIndex} queries={{ organization: OrganizationQuery.query }} render={renderSectionLoading} />
    </Route>

    <Route path="/" component={Main} getQueries={getMainQueries} render={renderMain} onEnter={routeEnterHandler} onChange={routeChangeHandler}>
      <Route path="authorize/:code" component={APIAccessTokenCodeAuthorize} queries={{ apiAccessTokenCode: APIAccessTokenCodeQuery.query }} />
    </Route>
  </Router>
);
